import get from "lodash/get";
import find from "lodash/find";
import isEqual from "lodash/isEqual";
import groupBy from "lodash/groupBy";

import { REVIEW_PASSWORD, defaultFirebaseProjectId } from "../constants";
import moment from "moment";
import {
  PROGRAMMING,
  getAthleteFromId,
  getAthleteInfo,
} from "../views/coaching-portal/tasks/task-constants";
import { setSession } from "../utils/session-helper";
import { validURL } from "../utils/string-helpers";

const Cycle = (app) => {
  app
    .post(
      "/cycle/send/",
      async ({ api, assert, res, req, load, locals, flash }) => {
        const { body, query } = req;

        const { cycleId, programOrder, destinationCycleId, recipients } =
          req.body;

        const hasOrganizationId = !!locals.activeUser.organizationId;

        let queryLoadedCycle = {
          cycle: cycleId,
        };
        //in this step we want to capture the athlete (the only recipient) this is only for program builder
        if (recipients && recipients.length === 1 && hasOrganizationId) {
          queryLoadedCycle = {
            ...queryLoadedCycle,
            athleteFromOrganization: recipients[0],
          };
        }
        const loadedCycle = await load("editCycle", queryLoadedCycle);

        const destinationCycle = await load("editCycle", {
          cycle: destinationCycleId,
        });

        // Sending/Pushing Individual Program
        if (programOrder) {
          // Make sure, user is not sending the program to the same cycle
          assert(
            !isEqual(destinationCycleId, cycleId),
            400,
            "Cannot send to the same cycle, use duplication instead."
          );

          // Make sure the program is not empty
          loadedCycle.programs[programOrder - 1].sessions.map((sessionItem) => {
            assert(
              get(sessionItem, "exercises", []).length,
              400,
              "Pushed programs must not have empty workouts. Please edit your program."
            );
          });
        }
        // If programOrder is undefined we send all Programs.
        else {
          // Make sure the cycle has no empty programs
          loadedCycle.programs.map((programItem) => {
            programItem.sessions.map((sessionItem) => {
              assert(
                get(sessionItem, "exercises", []).length,
                400,
                "Pushed programs must not have empty workouts. Please edit your program."
              );
            });
          });
        }
        try {
          await api("cycle-send", {
            method: "POST",
            body,
          });
          flash(
            "success",
            `Successfully sent ${body.programOrder ? "program" : "cycle"}`
          );
          res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
        } catch (e) {
          console.log("caught in fetch", e.message);
          throw e;
        }
      }
    )

    .post(
      "/cycle/:id",
      async ({ api, assert, flash, req, res, load, locals }) => {
        const {
          query: { proxyAthlete, organizationCoach = undefined },
        } = locals;

        const queryOrganizationCoach = organizationCoach
          ? `&organizationCoach=${organizationCoach}`
          : "";

        const hasOrganizationId = !!locals.activeUser.organizationId;
        const cycle = await load("editCycle", {
          cycle: req.params.id,
          proxyAthlete,
          athleteFromOrganization: hasOrganizationId ? proxyAthlete : undefined,
        });

        const isPremiumCoach = locals.activeUser.hasRole("coachCanSubmit");
        const isCoach = locals.activeUser.hasRole("coach");

        if (proxyAthlete) {
          // Check if the athlete is inactive and prohibit programs if so
          const athlete = find(
            locals.activeUser.athletes,
            (athlete) => String(athlete._id) === String(proxyAthlete)
          );
          const athleteInactive =
            athlete && athlete.status && athlete.status.inactive;
          assert(
            !athleteInactive,
            400,
            "Can't create blocks for inactive athletes"
          );
        }

        assert(cycle, 400, "Program does not exist, try 'Build Program' again");
        const program = req.body || {};
        let redirectPath, flashMessage;

        if (program.source == "review") {
          // Submit a cycle/program for review
          assert(
            program.password === REVIEW_PASSWORD,
            400,
            "Wrong admin password."
          );

          assert(
            cycle.programs.length &&
              cycle.programs.some((p) => p.status !== "published"),
            400,
            "No block to approve."
          );
          cycle.status = "published";
          cycle.programs = cycle.programs.map((program) => {
            program.status = "published";
            return program;
          });
          if (cycle.type !== "standard") cycle.type = "premium";

          cycle.createdDate = Date.now();

          redirectPath = `/dashboard/program-library/program-overview/${cycle._id}?program=${program.order}`;
          flashMessage = "Successfully published";
        } else {
          // asserting required fields are present
          // common asserts

          // An athlete can't add programs to a coach-made cycle
          assert(
            !(cycle.type === "coach" && !isCoach),
            400,
            "Blocks can't be added to Coach programs"
          );

          // coach asserts
          if (isPremiumCoach && cycle.type !== "coach") {
            if (cycle.programs)
              assert(
                cycle.programs.length <= 4,
                400,
                "Maximum of 4 blocks per program"
              );
          }

          program.modifiedDate = Date.now();
          program.createdDate = program.createdDate || Date.now();
          program.exercises = [];

          if (!cycle.programs) cycle.programs = [];
          cycle.programs.push({
            name: `Block ${cycle.programs.length + 1}`,
            numberOfWeeks: cycle.numberOfWeeks || 4,
            sessionsPerWeek: cycle.sessionsPerWeek || 1,
            description: "Some description...",
            modifiedDate: Date.now(),
            createdDate: Date.now(),
            exercises: [],
          });

          redirectPath = proxyAthlete
            ? // then its made for a particular athlete
              `/athlete-portal/programs/edit-program/${cycle._id}?athlete=${proxyAthlete}&program=${cycle.programs.length}${queryOrganizationCoach}`
            : cycle.type === "coach" && cycle.status !== "pushed"
            ? // then its made as a template
              `/coaching-portal/templates/edit-program/${cycle._id}?program=${cycle.programs.length}`
            : cycle.type === "subscription" && cycle.status !== "published"
            ? // then its made as a template but from store section
              `/coaching-portal/store/edit-program/${cycle._id}?program=${cycle.programs.length}`
            : // then it should go to dashboard
              `/dashboard/program-builder/edit-program/${cycle._id}?program=${cycle.programs.length}`;
          flashMessage = "Successfully configured block";
        }

        try {
          const updatedcycle = await api(
            `cycle/${cycle._id}/?proxyAthlete=${proxyAthlete}`,
            {
              method: "PUT",
              body: {
                programs: cycle.programs,
                status: cycle.status,
                modifiedDate: Date.now(),
              },
            }
          );
          const cycleJson = await updatedcycle.json();
          locals.editCycle = cycleJson;
          flash("success", flashMessage);
        } catch (e) {
          console.log("error in program/:id", e);
          flash("error", e.message);
        }

        res.redirect(redirectPath);
      }
    )
    .post("/cycle", async ({ api, assert, res, req, load, locals, flash }) => {
      let { body, files, query = {} } = req,
        flashMessage;
      const {
        isTemplate,
        proxyAthlete,
        isSubscription,
        organizationCoach = undefined,
        storeType,
      } = query;

      if (!body._id) {
        if (storeType) {
          body.storeType = storeType;

          // Make sure the single-workout has only 1 of each
          if (storeType === "single-workout") {
            body.numberOfWeeks = 1;
            body.sessionsPerWeek = 1;
          }
        }

        // need active cycles to check name uniqueness
        if (proxyAthlete || isTemplate || isSubscription) {
          // Check if the athlete is inactive and prohibit programs if so
          const athlete = find(
            locals.activeUser.athletes,
            (athlete) => athlete._id === proxyAthlete
          );
          const athleteInactive =
            athlete && athlete.status && athlete.status.inactive;
          assert(
            !athleteInactive,
            400,
            "Can't create blocks for inactive athletes"
          );

          body.type = isSubscription ? "subscription" : "coach";
          body.owner = proxyAthlete;
        }

        // asserting required fields are present
        // @format => assert(condition, STATUS_CODE, message)
        // asserts if condition is false
        if (!body.name) {
          flash("error", "Program name cannot be blank");
          return res.refreshWith({ submitError: true }, { url: "back" });
        }
        if (body.type && body.type !== "user") {
          if (!body.description) {
            flash("error", "Please provide program description");
            return res.refreshWith({ submitError: true }, { url: "back" });
          }
        }
        // setting defaults
        const tags = JSON.parse(body.tags);
        if (tags.length > 8) {
          flash("error", "Maximum 8 tags allowed");
          return res.refreshWith({ submitError: true }, { url: "back" });
        }
        body.status = body.status || (isSubscription ? "draft" : "created");
        // difficulty
        body.difficulty = {
          1: "Beginner",
          2: "Intermediate",
          3: "Expert",
        }[body.difficulty];
        body.type =
          body.type ||
          {
            admin: "standard",
            coachCanSubmit: "premium",
            ambassador: "user",
            coach: "user", // if body.type is not already set to 'coach' above, then the coach is just creating a cycle as a user
            user: "user",
          }[locals.activeUser.leadRole];

        body.createdBy = locals.activeUserToRequest._id;
        body.owner = body.owner || body.createdBy;
        body.goals = tags;
        // Make sure limit tag 8
        body.programs = [
          {
            name: "Block 1",
            goals: "",
            numberOfWeeks: body.numberOfWeeks,
            sessionsPerWeek: body.sessionsPerWeek,
            description: "",
            squatFrequency: 0,
            benchFrequency: 0,
            deadliftFrequency: 0,
            isEquippedLifting: true,
            modifiedDate: Date.now(),
            createdDate: Date.now(),
            exercises: [],
          },
        ];

        if (body.video) {
          assert(validURL(body.video), 400, "The video URL is not valid");
        }

        body = await (
          await api("cycle", {
            method: "POST",
            body,
            files,
            proxyAthlete,
          })
        ).json();
        flashMessage = `Successfully created`;
      } else {
        flashMessage = `Successfully selected`;
      }

      let organizationQuery = ``;

      if (organizationCoach) {
        organizationQuery = `&organizationCoach=${organizationCoach}`;
      }

      const redirectPath = proxyAthlete
        ? // then its made for a particular athlete
          `/athlete-portal/programs/edit-program/${body._id}?athlete=${proxyAthlete}&program=1` +
          organizationQuery
        : body.type === "coach" && body.status !== "pushed"
        ? // then its made as a template
          `/coaching-portal/templates/edit-program/${body._id}?program=1`
        : body.type === "subscription" && body.status !== "published"
        ? // then its made as a template but from store section
          `/coaching-portal/store/edit-program/${body._id}?program=1`
        : // then it should go to dashboard
          `/dashboard/program-builder/edit-program/${body._id}?program=1`;

      flash("success", flashMessage);
      res.redirect(`${redirectPath}&submitError=false`);
    })

    .post(
      "/cycle/edit/:id",
      // Edit a cycle from the edit-cycle-settings modal
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        let {
          body,
          files,
          query: { athlete, coaching },
        } = req;
        assert(body.name, 400, "Program name required");
        assert(body.description, 400, "Program description required");

        if (
          ["premium", "standard"].includes(body.type) &&
          body.status === "published"
        ) {
          const cycle = await load("editCycle", { cycle: req.params.id });
          cycle.programs = cycle.programs.map((program) => {
            program.status = "published";
            return program;
          });
          body = Object.assign({}, cycle, body);
        }

        body.goals = JSON.parse(body.tags);
        if (!body.goals.length) {
          body.goals = "clean_tags";
        }
        body.difficulty = {
          1: "Beginner",
          2: "Intermediate",
          3: "Expert",
        }[body.difficulty];

        if (body.video) {
          assert(validURL(body.video), 400, "The video URL is not valid");
        }

        try {
          const response = await api(`cycle/${req.body._id}`, {
            method: "PUT",
            body,
            files,
          });
          const editCycle = await response.json();
          setSession(session, "editCycle", editCycle, "2 seconds");
        } catch (e) {
          console.log("Error", e);
        }
        flash("success", "Successfully updated");

        if (
          athlete ||
          (body.type === "coach" && body.status !== "pushed") ||
          coaching
        )
          res.refreshWith({ modal: "" }, { url: "back" });
        else res.redirect(`/dashboard/program-builder`);
      }
    )
    .get(
      "/cycle/unpublish/:id",
      async ({ api, assert, res, req, load, locals, flash }) => {
        let { id } = locals.params;
        const store = req.query.store || false;
        assert(id, 400, "Program id is required");

        if (
          process.browser &&
          confirm("Are you sure you want to unpublish this Program?")
        ) {
          try {
            const updatedcycle = await api(`cycle-unpublish`, {
              method: "PUT",
              body: { id, store },
            });
          } catch (e) {
            console.log("Error", e);
          }
          flash("success", "Successfully un-published");
          if (store) {
            res.refreshWith({ status: "draft" }, { url: "back" });
          } else {
            res.redirect(`/dashboard/program-builder`);
          }
        } else {
          res.refreshWith({ status: "published" }, { url: "back" });
        }
      }
    )
    .get(
      "/cycle/publish/:id",
      async ({ api, assert, res, req, load, locals, flash }) => {
        let { id } = locals.params;
        assert(id, 400, "Program id is required");

        if (
          process.browser &&
          confirm("Are you sure you want to publish this Program?")
        ) {
          try {
            const updatedcycle = await api(`cycle-publish`, {
              method: "PUT",
              body: { id },
            });
            flash("success", "Successfully published");
            res.refreshWith({ status: "published" }, { url: "back" });
          } catch (e) {
            flash("error", e.message);
            res.refreshWith({ status: "draft" }, { url: "back" });
          }
        } else {
          res.refreshWith({ status: "draft" }, { url: "back" });
        }
        // res.redirect(`/coaching-portal/store`);
      }
    )
    .get(
      "/cycle/store/:id",
      async ({ api, assert, res, req, load, locals, flash }) => {
        let { id } = locals.params;
        assert(id, 400, "Program ID is required");

        try {
          const updatedcycle = await api(`cycle-store`, {
            method: "POST",
            body: { id, user: locals.activeUserToRequest._id },
          });
        } catch (e) {
          console.log("Error", e);
        }
        flash("success", "Successfully copied to the store");
        res.redirect(`/coaching-portal/store?status=draft`);
      }
    )
    .post(
      "/cycle/edit/program/:cycleId",
      // Edit a program
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        let body = req.body;
        const cycleId = locals.params.cycleId;

        let queryEditCycle = {
          cycle: cycleId,
          proxyAthlete: req.query.athlete,
        };

        if (req.query.athlete && locals.activeUser.organizationId) {
          queryEditCycle = {
            ...queryEditCycle,
            athleteFromOrganization: req.query.athlete,
          };
        }

        await load("editCycle", queryEditCycle);

        locals.editCycle.programs[locals.query.program - 1] = Object.assign(
          locals.editCycle.programs[locals.query.program - 1],
          req.body,
          { modifiedDate: Date.now() }
        );

        locals.editCycle.programs.forEach((program) => {
          if ("undefined" in program) delete program["undefined"];
        });

        try {
          const response = await api(`cycle/${cycleId}`, {
            method: "PUT",
            body: {
              programs: locals.editCycle.programs,
              modifiedDate: Date.now(),
            },
          });
          const cycle = await response.json();
          setSession(session, "editCycle", cycle);
        } catch (e) {
          console.log("Error: ", e);
        }
        flash("success", "Successfully updated");
        res.refreshWith({ modal: "" }, { url: "back" });
      }
    )
    .get(
      "/cycle/duplicate/:cycleId",
      // Duplicate a cycle
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const cycle = locals.params.cycleId;
        const store = req.query.store || false;
        const user = locals.activeUserToRequest._id;

        await api("cycle-duplicate-cycle", {
          method: "POST",
          body: { cycle, store, user },
        });
        flash("success", "Program duplicated");
        res.backDontScroll();
      }
    )
    .get(
      "/cycle/program/duplicate/:cycleId",
      // Duplicate a program within a cycle
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const { cycleId } = locals.params;

        const program = locals.query.program || undefined;
        const user = locals.activeUserToRequest._id;
        assert(cycleId, 400, "Program Required");
        assert(program, 400, "Block Required");
        delete program.progress;
        delete program.instances;

        const updatedcycle = await api(`cycle-duplicate-program`, {
          method: "POST",
          body: { program, cycle: cycleId, user },
        });
        flash("success", "Block duplicated");
        res.backDontScroll();
      }
    )
    .get(
      "/cycle/programs/reorder/:cycleId/:from/:to",
      async ({ req, res, locals, api, load, assert, session }) => {
        const { from, to, cycle } = req.params;

        await api(`cycle-program-order`, { method: "POST", body: req.params });

        res.backDontScroll();
      }
    )
    .get(
      "/cycle/like/:cycleId",
      // Like or unlike a cycle
      async ({ api, assert, res, req, load, locals }) => {
        const { params } = req;
        assert(params.cycleId, 400, "Program Required");

        await api(`like-cycle`, {
          method: "PUT",
          body: {
            cycle: params.cycleId,
            user: locals.activeUser._id,
          },
        });

        // TODO: this redirect will have to be different on the library page
        res.backDontScroll();
        // res.redirect(`/dashboard/program-library/program-overview/${params.cycleId}`);
      }
    )
    .get(
      "/cycle/addWorkout/:cycleId",
      // Add an empty workout to a week
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const { params, query } = req;
        assert(params.cycleId, 400, "Program Required");

        const response = await api(`cycle-add-workout`, {
          method: "POST",
          body: {
            cycle: params.cycleId,
            program: query.program,
            week: query.week,
            user: locals.activeUserToRequest._id,
          },
        });
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);

        flash(
          "success",
          `${query.source === "addWeek" ? "New week" : "Workout"} added`
        );
        res.refreshWith({ week: query.week }, { url: "back" });
      }
    )
    .post(
      "/cycle/program/addWeek",
      async ({ api, assert, res, req, flash, locals, momentTZ, session }) => {
        const { cycle, program, sessionsPerWeek, initNewWeek, newWeeks } =
          req.body;
        const { activeUserToRequest } = locals;
        assert(cycle?._id && cycle.programs, 400, "Program Required");
        const createdBy = cycle.createdBy?._id || cycle.createdBy;
        const owner = cycle.owner?._id || cycle.owner;
        assert(
          createdBy == activeUserToRequest._id ||
            owner == activeUserToRequest._id,
          400,
          "Cannot update another user's program"
        );

        const sessionsToAdd = sessionsPerWeek || 1;
        const programs = [...cycle.programs];
        const index = programs.findIndex(({ order }) => order == program);
        assert(index !== -1, 400, "Block does not exist");
        const week = initNewWeek + newWeeks;
        const startingWeekToIterate = initNewWeek + 1;
        for (let w = 0; w < newWeeks; w++) {
          const currentWeek = startingWeekToIterate + w;
          let maxSession = 0;
          for (let i = 0; i < sessionsToAdd; i++) {
            // get the last session that exists
            const lastSession = programs[index].sessions
              .filter((session) => session.week == currentWeek)
              .reduce(
                (prev, current) =>
                  prev.order > current.order ? prev : current,
                { order: 0 }
              );
            maxSession = Number(lastSession.order);
            // Prohibit more workouts than days in a week
            assert(
              maxSession < 7,
              400,
              "Cannot add more than 7 workouts to a week"
            );

            // add the session to the program
            programs[index].sessions.push({
              week: currentWeek,
              date: momentTZ(0).add(currentWeek, "weeks").format(),
              order: maxSession + 1,
              exercises: [],
            });
          }
        }

        programs[index].modifiedDate = Date.now();
        const response = await api(`cycle/${cycle._id}`, {
          method: "PUT",
          body: {
            programs,
            modifiedDate: Date.now(),
          },
        });
        const message = newWeeks > 1 ? "weeks" : "week";
        const updatedCycle = await response.json();
        setSession(session, "editCycle", updatedCycle);
        flash("success", `New ${message} added`);
        res.refreshWith({ week }, { url: "back" });
      }
    )
    .post(
      "/cycle/program/duplicateWeek",
      // Copy the sessions from one week to an empty week
      async ({ api, assert, flash, res, req, load, locals, session }) => {
        const { body } = req;
        assert(
          get(body, "to.length"),
          400,
          "Select at least one place where you want to duplicate to."
        );
        // if body.error is set, means that weeks are not valid
        assert(!body.error, 400, body.error);

        const response = await api(
          body.calendarMode
            ? `copy-week?proxyAthlete=${body.proxyAthlete}`
            : `cycle-duplicate-week`,
          { method: "POST", body }
        );
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        flash("success", "Successfully duplicated");
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/cycle/program/duplicateWorkout",
      // Copy a workout to the same week
      async ({ api, assert, flash, res, req, load, locals, session }) => {
        const { body } = req;
        assert(
          get(body, "to.length"),
          400,
          "Select at least one place where you want to duplicate to."
        );
        const response = await api(`cycle-duplicate-workout`, {
          method: "POST",
          body,
        });
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        setSession(session, "editExercises", data.programExercises);

        flash("success", "Successfully duplicated");
        res.refreshWith(
          { modal: "", isExercise: false },
          { url: "back", hash: "#_" }
        );
      }
    )
    .post(
      "/cycle/store/reorder",
      // Reorder Position in Store
      async ({ api, assert, flash, res, req, load, locals, session }) => {
        let { body } = req;
        assert(
          body.orderedCycles,
          400,
          "You need to provide an array with the order of the cycles"
        );

        if (
          locals.activeUser.organizationId &&
          locals.activeUserToRequest &&
          String(locals.activeUser._id) !==
            String(locals.activeUserToRequest._id)
        ) {
          body = {
            ...body,
            userOrganizationId: locals.activeUserToRequest._id,
          };
        }

        await api(`cycle-store-reorder`, {
          method: "PUT",
          body,
        });

        // TODO: Fix weird behavior when refreshing page that causes programs disordering again but only in the DOM (not in the API)
        // flash("success", "Successfully reordered");
        // res.backDontScroll();
      }
    )
    .get(
      "/cycle/reorder-workout/:cycleId/:program/:week/:sessionOrder/:to",
      // In Editor, reorder a workout up or down by one
      async ({ api, assert, res, req, load, locals, session }) => {
        const { params, query } = req;
        assert(params.cycleId, 400, "Program Required");

        const response = await api(`cycle-session-order`, {
          method: "POST",
          body: {
            cycle: params.cycleId,
            program: params.program,
            week: params.week,
            session: params.sessionOrder,
            to: params.to,
            user: locals.activeUserToRequest._id,
          },
        });
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        setSession(session, "editExercises", data.programExercises);

        res.backDontScroll();
      }
    )
    .get(
      "/cycle/delete-workout/:cycleId/:program/:week/:order",
      // In Editor, delete a workout
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const { params, query } = req;
        assert(params.cycleId, 400, "Program Required");
        if (
          process.browser &&
          !confirm("Are you sure you want to delete this workout?")
        ) {
          return res.backDontScroll();
        }

        const response = await api(`cycle-delete-workout`, {
          method: "POST",
          body: {
            cycle: params.cycleId,
            program: params.program,
            week: params.week,
            session: params.order,
            user: locals.activeUserToRequest._id,
          },
        });
        const data = await response.json();
        setSession(session, "editCycle", data.cycle);
        flash("success", "Workout deleted");

        res.backDontScroll();
      }
    )
    .get(
      "/cycle/delete-week/:cycleId/:program/:week",
      // In Editor, delete a week
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const { params, query } = req;
        assert(params.cycleId, 400, "Program Required");
        if (
          process.browser &&
          !confirm(
            `Are you sure you want to delete entire week ${params.week}?`
          )
        ) {
          return res.backDontScroll();
        }
        try {
          const response = await api(`cycle-delete-week`, {
            method: "POST",
            body: {
              cycle: params.cycleId,
              program: params.program,
              week: params.week,
              user: locals.activeUserToRequest._id,
            },
          });
          const data = await response.json();
          setSession(session, "editCycle", data.cycle);

          flash("success", "Week deleted");
        } catch (err) {
          flash("error", "Could not delete week");
        }
        res.backDontScroll();
      }
    )
    .get(
      "/cycle/delete-single-workout/:cycleId/",
      // In Editor, delete a week
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const { params, query } = req;
        assert(params.cycleId, 400, "Program Required");
        if (
          process.browser &&
          !confirm(`Are you sure you want to clear the entire workout?`)
        ) {
          return res.backDontScroll();
        }
        try {
          const response = await api(`cycle-delete-single-workout`, {
            method: "POST",
            body: {
              cycle: params.cycleId,
              user: locals.activeUserToRequest._id,
            },
          });
          const data = await response.json();
          setSession(session, "editCycle", data.cycle);

          flash("success", "Workout deleted");
        } catch (err) {
          flash("error", "Could not delete workout");
        }
        res.backDontScroll();
      }
    )
    .post(
      "/cycle/program/delete-weeks",
      // In Editor, delete multiple weeks
      async ({ api, assert, res, req, locals, flash, session }) => {
        const { body } = req;
        const { cycle, program, selectedWeeks } = body;
        assert(cycle, 400, "Program Required");
        assert(program, 400, "Block Required");
        assert(selectedWeeks?.length, 400, "Weeks Required");

        try {
          const response = await api("cycle-delete-weeks", {
            method: "POST",
            body: {
              cycle,
              program,
              weeks: selectedWeeks,
              user: locals.activeUserToRequest._id,
            },
          });
          const data = await response.json();
          setSession(session, "editCycle", data.cycle);

          flash("success", "Weeks deleted");
        } catch (err) {
          flash("error", "Could not delete weeks");
        }
        res.backDontScroll();
      }
    )
    .post(
      "/cycle/add-to-calendar/:cycleId",
      // From the add-to-calender modal, schedule all the sessions in an array of programs
      async ({ api, assert, res, req, load, locals, flash }) => {
        const { params, body } = req;
        assert(params.cycleId, 400, "Program Required");
        // Filter out invalid programs, then get rid of extraneous info
        const programs = body.formPrograms
          .filter((program) => {
            if (program.isValid === "true") {
              // Check that all the workouts have at least one exercise
              program.sessions.forEach((session) => {
                assert(
                  get(session, "exercises", []).length,
                  400,
                  "Block must not have empty workouts. Please edit your Block."
                );
              });
              return true;
            }
          })
          .map((program) => {
            return {
              program: program.order,
              startDate: program.startDate,
              dates: program.dates,
            };
          });
        assert(programs.length, 400, "Block required");

        await api(`cycle-add-to-calendar`, {
          method: "POST",
          body: {
            cycle: params.cycleId,
            user: locals.activeUser._id,
            programs,
            daysToBeOverwritten: body.daysToBeOverwritten,
          },
        })
          .then((response) => {
            const reportToMobileApp = () => {
              //close window in mobile app - start
              const message = {
                type: "addToCalendarSuccess",
                message: "close",
              };
              if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(JSON.stringify(message));
              }

              //close window in mobile app - end
            };

            response.status < 400 &&
              flash(
                "success",
                `Program${
                  programs.length > 1 ? "s" : ""
                } successfully scheduled`
              );

            response.status < 400 && reportToMobileApp();

            response.status > 399 && flash("error", response.statusText);
          })
          .catch((err) => {
            flash("error", err.message);
          });
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .get(
      "/cycle/coach-add-session/:cycleId/:programNumber/:numberOfWeeks",
      // From the program editor footer, add a workout to each week
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const {
          params: { cycleId, programNumber, numberOfWeeks },
        } = req;

        for (let i = 1; i < parseInt(numberOfWeeks) + 1; i++) {
          await api(`cycle-add-workout`, {
            method: "POST",
            body: {
              program: programNumber,
              week: i,
              cycle: cycleId,
              user: locals.activeUserToRequest._id,
            },
          });
        }

        flash("success", "Workout added to each week");
        res.backDontScroll();
      }
    )
    .get(
      "/cycle/coach-remove-session/:cycleId/:programNumber/:sessionNumber/:numberOfWeeks",
      // From the program editor footer, delete a workout from each week
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const {
          params: { cycleId, programNumber, sessionNumber, numberOfWeeks },
        } = req;

        for (let i = 1; i < parseInt(numberOfWeeks) + 1; i++) {
          await api(`cycle-delete-workout`, {
            method: "POST",
            body: {
              program: programNumber,
              week: i,
              cycle: cycleId,
              session: sessionNumber,
              user: locals.activeUserToRequest._id,
            },
          });
        }

        flash("success", "Workout deleted from each week");

        res.backDontScroll();
      }
    )
    .get(
      "/cycle/coach-reorder-week/:cycleId/:programNumber/:from/:to",
      // From the program editor footer, move a week back or forth in order
      async ({ api, assert, res, req, load, locals, session }) => {
        const {
          params: { cycleId, programNumber, from, to },
        } = req;

        await api("cycle-move-week", {
          method: "POST",
          body: {
            cycle: cycleId,
            program: programNumber,
            from,
            to,
            user: locals.activeUserToRequest._id,
          },
        });

        res.backDontScroll();
      }
    )
    // .get("/cycle/coach-duplicate-week/:cycleId/:programNumber/:week",
    //   // From the program editor footer, copy a week and add it to the end of a program
    //   async ({ api, assert, res, req, load, locals }) => {
    //     const { params : {cycleId, programNumber, week}} = req;
    //
    //     await api('cycle-duplicate-week', { method: "POST", body: {
    //       cycle: cycleId,
    //       program: programNumber,
    //       week
    //     }})
    //
    //     res.backDontScroll();
    //   }
    // )
    .get(
      "/cycle/delete-cycle/:cycle",
      // From the program editor footer, copy a week and add it to the end of a program
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const {
          params: { cycle },
        } = req;

        if (
          process.browser &&
          confirm("Are you sure you want to delete this program?")
        ) {
          const user = locals.activeUserToRequest._id;
          await api("cycle-delete", { method: "POST", body: { cycle, user } });

          flash("success", "Program deleted");
        }

        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .get(
      "/cycle/delete-program/:cycle/:program",
      // From the program editor footer, copy a week and add it to the end of a program
      async ({ api, assert, res, req, load, locals, flash, session }) => {
        const {
          params: { cycle, program },
          query: { isFromCoachingPortal },
        } = req;
        if (
          process.browser &&
          confirm(
            "By removing this block it will be permanently deleted from your library" +
              " but will not be removed from your calendar. Are you sure you want to delete this block?"
          )
        ) {
          await api("cycle-delete-program", {
            method: "POST",
            body: {
              cycle,
              program,
              isFromCoachingPortal,
            },
          });

          flash("success", "Block deleted");
        }
        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )
    .post(
      "/cycle/push/:cycle/:program/:athlete",
      // From the athlete's portal, push a program so the athlete can see it
      async ({ api, assert, res, req, load, locals, flash }) => {
        const {
          params: { cycle, program, athlete },
          body,
        } = req;
        const { activeUser, activeUserToRequest } = locals;
        const hasOrganizationId = !!locals.activeUser.organizationId;

        // Sending/Pushing Cycle

        let queryLoadedCycle = {
          cycle: req.params.cycle,
        };
        //in this step we want to capture the athlete (the only recipient) this is only for program builder
        if (athlete && hasOrganizationId) {
          queryLoadedCycle = {
            ...queryLoadedCycle,
            athleteFromOrganization: athlete,
          };
        }

        const loadedCycle = await load("editCycle", queryLoadedCycle);
        // Sending/Pushing Individual Program
        if (program) {
          loadedCycle.programs[program - 1].sessions.map((sessionItem) => {
            assert(
              get(sessionItem, "exercises", []).length,
              400,
              "Pushed blocks must not have empty workouts. Please edit your block."
            );
          });
        }
        // If program is undefined we send all programs.
        else {
          loadedCycle.programs.map((programItem) => {
            programItem.sessions.map((sessionItem) => {
              assert(
                get(sessionItem, "exercises", []).length,
                400,
                "Pushed blocks must not have empty workouts. Please edit your block."
              );
            });
          });
        }

        try {
          // CH-4085
          await (await api(`check-valid-cycle?id=${cycle}`)).json();
          await api("cycle-push", {
            method: "POST",
            body: {
              cycle,
              program,
              athlete,
              avatar:
                !!activeUser.avatar && !!activeUser.avatar.file
                  ? activeUser.avatar.file
                  : null,
              firebaseProjectId: !!activeUserToRequest.firebaseProjectId
                ? activeUserToRequest.firebaseProjectId
                : defaultFirebaseProjectId,
            },
          });
        } catch (e) {
          console.log("caught in fetch", e.message);
          throw e;
        }

        flash("success", "Successfully pushed block");

        try {
          const task = {
            type: PROGRAMMING,
            completed: false,
            date: moment.utc(body.date).valueOf(),
          };
          //find the athlete in the coach of the organization, or in the active user
          const athleteForTask = body.athleteForTask;
          if (!athleteForTask) {
            throw new Error("User not found. Cannot create task");
          }
          task.athlete = athleteForTask._id;
          task.athleteInfo = getAthleteInfo(athleteForTask);
          task.description = `New block required for ${athleteForTask.name}`;
          task.cycle = loadedCycle._id;
          if (program) {
            task.program = program;
            const fullProgram = loadedCycle.programs[program - 1];
            const weeks = groupBy(fullProgram.sessions, "week");
            task.programInfo = {
              _id: fullProgram._id,
              name: fullProgram.name,
              numberOfWeeks: Object.keys(weeks).length,
            };
          }

          await api("tasks", { method: "POST", body: task });
        } catch (e) {
          console.log("caught in fetch", e.message);
          flash("error", e.message);
        }

        res.refreshWith(
          { modal: "", pushedCycle: cycle, pushedProgram: program },
          { url: "back", hash: "#_" }
        );
      }
    )
    .post(
      "/cycle/store/manage-category",
      // Manage Category (Add/Edit)
      async ({ api, assert, flash, res, req, locals }) => {
        let { body } = req;
        assert(body.categoryName, 400, "You need to provide a category name");

        const { categoryName, categoryIndex } = body;

        if (!!locals.activeUser.organizationId) {
          body = {
            ...body,
            organizationUserId: locals.activeUserToRequest._id,
          };
        }

        await api(`cycle-categories`, {
          method: "PUT",
          body,
        });

        flash("success", "Successfully saved");
        res.refreshWith(
          {
            status: "published",
            "modal[type]": "",
            update: true,
            selectedCategoryIndex: categoryIndex,
            category: categoryName,
          },
          { url: "back", hash: "#_" }
        );
      }
    )
    .get(
      "/cycle/store/manage-category/:categoryIndex",
      // Manage Category (Delete)
      async ({ api, assert, flash, res, req, locals }) => {
        const {
          params: { categoryIndex },
        } = req;
        assert(categoryIndex, 400, "You need to provide a category to delete");

        let queryToDelete = `cycle-categories?categoryIndex=${categoryIndex}`;

        if (
          locals.activeUser.organizationId &&
          locals.activeUserToRequest &&
          String(locals.activeUser._id) !==
            String(locals.activeUserToRequest._id)
        ) {
          queryToDelete =
            queryToDelete +
            `&organizationCoachId=${locals.activeUserToRequest._id}`;
        }

        await api(queryToDelete, {
          method: "DELETE",
        });

        flash("success", "Successfully deleted");

        res.refreshWith(
          { status: "published", deleteIndex: categoryIndex },
          { url: "back" }
        );
      }
    )
    .post(
      "/cycle/store/sort-category",
      // Reorder Position of Categories
      async ({ api, assert, flash, res, req }) => {
        const { body } = req;

        assert(
          body.sortedCategories,
          400,
          "You need to provide at least one category"
        );

        await api(`cycle-sort-categories`, {
          method: "PUT",
          body,
        });

        flash("success", "Successfully sorted");
        res.refreshWith(
          { status: "published", "modal[type]": "", update: true },
          { url: "back", hash: "#_" }
        );
      }
    )

    .post(
      "/cycle/store/popular-program",
      async ({ api, assert, flash, res, req }) => {
        const { body } = req;

        assert(body.programId, 400, "You need to provide a program");

        await api(`cycle-categories-popular`, {
          method: "POST",
          body,
        });

        flash("success", "Program set as most popular");

        res.refreshWith(
          { status: "published", "modal[type]": "", favorite: body.programId },
          { url: "back", hash: "#_" }
        );
      }
    )
    .post(
      "/cycle/edit/program-workout-name/:cycleId",
      async ({ api, res, req, load, locals, flash, session }) => {
        const body = req.body;
        const cycleId = locals.params.cycleId;

        const sessionOrder = req.query.sessionOrder;
        const sessionWeek = req.query.sessionWeek;
        const { updatedWorkoutName = "" } = body;

        let queryEditCycle = {
          cycle: cycleId,
          proxyAthlete: req.query.athlete,
        };

        if (req.query.athlete && locals.activeUser.organizationId) {
          queryEditCycle = {
            ...queryEditCycle,
            athleteFromOrganization: req.query.athlete,
          };
        }

        await load("editCycle", queryEditCycle);

        const program = locals.editCycle?.programs[locals.query.program - 1];
        const sessions = program && program.sessions ? program.sessions : [];
        const sessionsToUpdate = [...sessions];

        const indexToUpdate = sessionsToUpdate.findIndex(
          (item) => item.order === sessionOrder && item.week === sessionWeek
        );

        if (indexToUpdate >= 0 && indexToUpdate < sessionsToUpdate.length) {
          sessionsToUpdate[indexToUpdate] = {
            ...sessionsToUpdate[indexToUpdate],
            workoutName: updatedWorkoutName,
          };

          locals.editCycle.programs[locals.query.program - 1] = Object.assign(
            locals.editCycle.programs[locals.query.program - 1],
            { sessions: sessionsToUpdate },
            { modifiedDate: Date.now() }
          );

          locals.editCycle.programs.forEach((program) => {
            if ("undefined" in program) delete program["undefined"];
          });

          try {
            const response = await api(`cycle/${cycleId}`, {
              method: "PUT",
              body: {
                programs: locals.editCycle.programs,
                modifiedDate: Date.now(),
              },
            });
            const cycle = await response.json();
            setSession(session, "editCycle", cycle);
          } catch (e) {
            console.log("Error editing the workout name: ", e);
          }
        }

        flash("success", "Successfully updated");
        res.refreshWith({ modal: "" }, { url: "back" });
      }
    );
};

export default Cycle;
